/**
 * The providers, in the one order that works.
 *
 * `QueryClientProvider` is outermost because `AuthProvider` *is* a query. `AuthProvider` has to
 * be inside the router, because it owns the global 401 redirect and therefore calls
 * `useNavigate`. Those two facts together are why it cannot be mounted here: it is mounted by the
 * router, as the shell's parent (see `router.tsx`).
 *
 * The client and the router are passed in rather than created here so a test can supply its own —
 * a `MemoryRouter` and a fresh cache per test — without the module creating a `BrowserRouter` as
 * a side effect of being imported.
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router';

import { ThemeProvider, ToastProvider } from '@catalog/ui';

import { isApiError } from './api/client.js';

/**
 * The shared query client.
 *
 * `staleTime` is set per query (see `api/query-keys.ts`) rather than globally, because the
 * catalog, a transcript and the session have genuinely different answers. What is global is the
 * retry policy, and the rule is the useful half of it: **never retry a request the server
 * answered.** A 404 will not become a 200, a 401 is the session layer's business, and retrying a
 * 429 is the one thing guaranteed to make a rate limit worse.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (isApiError(error) && error.kind === 'http') return false;

          return failureCount < 2;
        },
        // Browsing a catalog that only changes with an ingestion run does not need a refetch
        // because the window regained focus.
        refetchOnWindowFocus: false,
      },
      mutations: {
        // Nothing this app mutates is safe to replay blindly: a second sign-in attempt consumes a
        // rate-limit token, and a second invite acceptance is a 404.
        retry: false,
      },
    },
  });
}

export interface AppProps {
  readonly queryClient: QueryClient;
  readonly router: Parameters<typeof RouterProvider>[0]['router'];
}

export function App({ queryClient, router }: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
